import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/Button';
import { useWallet } from '../../hooks/useWallet';
import { CHAIN_CONFIGS } from '../../utils/constants';
import { useWeb3Modal } from '@web3modal/wagmi/react';

export const Header: React.FC = () => {
  const { walletInfo, connectWallet, disconnectWallet, displayAddress, isConnecting } = useWallet();
  const { open } = useWeb3Modal();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Get current chain name
  const currentChainName = walletInfo.chainId 
    ? CHAIN_CONFIGS[Object.keys(CHAIN_CONFIGS).find(key => 
        CHAIN_CONFIGS[key].chainId === walletInfo.chainId
      ) || '']?.name || 'Unknown Network'
    : '';
    
  // Navigation links
  const navLinks = [
    { path: '/', label: 'Gateway' },
    { path: '/portfolio', label: 'Portfolio' }
  ];
  
  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <header className="border-b border-gray-700/30 backdrop-blur-md shadow-md" style={{
      background: 'rgba(17, 24, 39, 0.6)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)'
    }}>
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        <div className="flex items-center">
          {/* Logo and Title - Clickable on mobile to toggle menu */}
          <div 
            className="flex items-center space-x-2 cursor-pointer md:cursor-default relative group"
            onClick={() => {
              if (window.innerWidth < 768) { // Only toggle on mobile
                toggleMobileMenu();
              }
            }}
          >
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center shadow-lg shadow-gray-900/30 border border-gray-600/30 relative">
              <img src="/images/wshido.png" alt="Shido Gateway" className="w-8 h-8" />
              
              {/* Mobile menu indicator - only visible on mobile */}
              <div className="md:hidden absolute -right-1 -bottom-1 w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center shadow-lg">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-3 w-3 text-white transition-transform duration-300 ${mobileMenuOpen ? 'rotate-180' : 'rotate-0'}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-white to-primary-200 bg-clip-text text-transparent">Shido Gateway</h1>
              <p className="text-xs text-gray-300">Cross-chain token bridge for Shido Network</p>
            </div>
          </div>
          
          {/* Navigation Links */}
          <nav className="ml-10 hidden md:flex space-x-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors duration-200 ${
                  location.pathname === link.path
                    ? 'text-primary-400 border-b-2 border-primary-400'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="flex items-center space-x-4">
          {walletInfo.isConnected ? (
            <div className="flex items-center space-x-3">
              <div className="hidden md:block">
                <div className="rounded-lg px-3 py-1.5 text-sm text-gray-300 border border-gray-600/30" style={{
                  background: 'rgba(55, 65, 81, 0.4)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)'
                }}>
                  {currentChainName}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-gray-600 text-gray-300"
                  onClick={() => open()}
                >
                  {displayAddress}
                </Button>
                <Button 
                  variant="danger" 
                  size="sm"
                  onClick={disconnectWallet}
                  title="Disconnect wallet"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </Button>
              </div>
            </div>
          ) : (
            <Button 
              onClick={connectWallet} 
              isLoading={isConnecting}
              size="sm"
            >
              Connect Wallet
            </Button>
          )}
        </div>
      </div>
      
      {/* Mobile Navigation Menu - Slides down when logo is clicked */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-700/30 animate-slideDown" style={{
          background: 'rgba(31, 41, 55, 0.5)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)'
        }}>
          <div className="container mx-auto px-4 py-3">
            <nav className="flex flex-col space-y-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium py-2 px-3 rounded-md transition-colors duration-200 ${
                    location.pathname === link.path
                      ? 'bg-gray-700 text-primary-400'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};
